import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Group, Rect, Text, Line } from 'react-konva';
import type {
  SequenceParticipant,
  SequenceMessage,
  SequenceFragment,
  SequenceParticipantGroup,
  Layer,
  ThemePalette,
  ContextMenuState,
  SequenceFragmentDragBaseline,
  SequenceDragApplyMetrics,
  KonvaInteractionEvent,
} from '../../types';
import { useDiagramStore } from '../../stores/diagramStore';
import { SEQUENCE, FONT_FAMILY } from '../../constants';
import { isInsideFragmentTree } from '../../utils/fragmentTree';
import { innermostContainingFragment } from '../../utils/fragmentLayout';
import { resolveSequenceMessageLabelWidth, buildMessageRowSnapshot, SequenceMessageRowSnapshot, assignSequenceMessageNumbers } from '../../utils/sequenceMessageLayout';
import { resolveMessageColor, resolveParticipantColor, resolveSequenceAppearanceMode, resolveSequencePalette } from '../../utils/sequenceAppearance';
import { screenToCanvasY, visibleCanvasRect } from '../../utils/sequenceSticky';
import { toRgba } from '../../utils/color';
import { InteractionPolicy } from '../../utils/interactionPolicy';
import { useSettingsStore } from '../../stores/settingsStore';
import { ParticipantComponent } from './ParticipantComponent';
import { MessageComponent } from './MessageComponent';
import { FragmentComponent } from './FragmentComponent';
import type { AltDividerDragMode } from '../../hooks/useSequenceDrag';

const SEQUENCE_MIN_LABEL_WIDTH = SEQUENCE.MIN_LABEL_WIDTH;
const ALT_DIVIDER_TOP_GAP = SEQUENCE.ALT_DIVIDER_TOP_GAP;
const ALT_DIVIDER_BOTTOM_GAP = SEQUENCE.ALT_DIVIDER_BOTTOM_GAP;
const ALT_DIVIDER_MESSAGE_PADDING = SEQUENCE.ALT_DIVIDER_MESSAGE_PADDING;

// ── Ref interfaces ──────────────────────────────────────────────────────
export interface SequenceCanvasRefs {
  isDraggingParticipantRef: React.MutableRefObject<string | null>;
  isDraggingMessageRef: React.MutableRefObject<string | null>;
  isDraggingFragmentRef: React.MutableRefObject<string | null>;
  activeFragmentResizeRef: React.MutableRefObject<string | null>;
  activeAltDividerDragRef: React.MutableRefObject<string | null>;
  fragmentResizeBaselineRef: React.MutableRefObject<SequenceFragmentDragBaseline | null>;
  altDividerDragBaselineRef: React.MutableRefObject<SequenceFragmentDragBaseline | null>;
  messageDragBaselineRef: React.MutableRefObject<SequenceFragmentDragBaseline | null>;
  dragReorderSnapshotRef: React.MutableRefObject<SequenceMessageRowSnapshot | null>;
  dragSelectedMessageIdsRef: React.MutableRefObject<string[]>;
  dragStartMessageYRef: React.MutableRefObject<{ id: string; y: number } | null>;
  reorderMessageDragKeyRef: React.MutableRefObject<boolean>;
}

// ── Callback interfaces ─────────────────────────────────────────────────
export interface SequenceCanvasCallbacks {
  selectItem: (id: string, multi?: boolean) => void;
  setContextMenu: React.Dispatch<React.SetStateAction<ContextMenuState>>;
  setInfoPanelFragmentId: (id: string | null) => void;
  setInfoPanelParticipantId: (id: string | null) => void;
  snapshotBeforeMutation: () => void;
  updateParticipant: (id: string, updates: Partial<SequenceParticipant>) => void;
  reorderParticipants: () => void;
  moveFragment: (id: string, deltaY: number) => void;
  setFragmentDropParentId: React.Dispatch<React.SetStateAction<string | null>>;
  setInlineEdit: React.Dispatch<React.SetStateAction<{ nodeId: string; field: 'label' | 'description' } | null>>;
  setInlineEdgeEdit: React.Dispatch<React.SetStateAction<{ edgeId: string } | null>>;
  setSeqMsgTooltip: React.Dispatch<React.SetStateAction<{ msgId: string; screenX: number; screenY: number } | null>>;
  handleSidebarHoverStart: (kind: 'node' | 'participant' | 'message', id: string) => void;
  handleSidebarHoverEnd: (kind: 'node' | 'participant' | 'message') => void;
  handleLifelineMouseDown: (participantId: string, y: number) => void;
  handleLifelineMouseUp: (participantId: string, y: number) => void;
  getSequenceMessageFootprint: (label: string, sourceId: string, targetId: string) => { above: number; below: number };
}

// ── Drag perf callbacks ─────────────────────────────────────────────────
export interface SequenceCanvasDragCallbacks {
  captureSequenceFragmentDragBaseline: (fragId: string) => SequenceFragmentDragBaseline;
  restoreSequenceFragmentDragBaseline: (baseline: SequenceFragmentDragBaseline) => void;
  beginSequenceDragPerf: (kind: 'message' | 'altDivider' | 'fragmentResize', id: string) => void;
  recordSequenceDragPerf: (kind: 'message' | 'altDivider' | 'fragmentResize', elapsedMs: number, movedMessages?: number, updatedFragments?: number) => void;
  endSequenceDragPerf: (kind: 'message' | 'altDivider' | 'fragmentResize') => void;
  scheduleFragmentResizeMove: (fragId: string, newEndY: number, mode: AltDividerDragMode) => void;
  cancelScheduledFragmentResizeMove: () => void;
  applyFragmentEndDrag: (fragId: string, newEndY: number, mode: AltDividerDragMode) => SequenceDragApplyMetrics;
  scheduleAltDividerDragMove: (fragId: string, newDividerY: number, mode: AltDividerDragMode) => void;
  cancelScheduledAltDividerDragMove: () => void;
  applyAltDividerDrag: (fragId: string, newDividerY: number, mode: AltDividerDragMode) => SequenceDragApplyMetrics;
}

// ── Message drag callbacks ──────────────────────────────────────────────
export interface SequenceCanvasMessageDragCallbacks {
  applyMessageDragWithReorder: (messageId: string, proposedY: number) => number;
  applyMessageDragWithPush: (messageId: string, proposedY: number) => number;
  ensureDraggedMessageFragmentExtents: (messageIds: string[]) => number;
  finalizeDraggedMessages: (messageIds: string[], fallbackAnchorId: string) => void;
  resolveDraggedMessageIds: (orderedIds: string[], draggedId: string) => string[];
}

// ── Props ───────────────────────────────────────────────────────────────
export interface SequenceCanvasProps {
  // Data
  participants: SequenceParticipant[];
  messages: SequenceMessage[];
  fragments: SequenceFragment[];
  participantGroups: SequenceParticipantGroup[];
  layers: Layer[];
  selectedItems: string[];

  // Display
  viewport: { x: number; y: number; scale: number };
  windowSize: { width: number; height: number };
  palette: ThemePalette;
  stickyMode: { topPinned: boolean; bottomPinned: boolean; stickyScale: number };
  stickyTopHeaderCanvasY: number;
  stickyBottomHeaderCanvasY: number;
  stickyMaskBands: { top?: { y: number; height: number }; bottom?: { y: number; height: number } };
  sequenceLifelineBottom: number;

  // State
  inlineEdit: { nodeId: string; field: 'label' | 'description' } | null;
  inlineEdgeEdit: { edgeId: string } | null;
  infoPanelParticipantId: string | null;
  sequenceDrag: { sourceParticipantId: string; sourceX: number; startY: number; currentX?: number } | null;
  fragmentDropParentId: string | null;
  isViewOnly: boolean;
  interactionPolicy: InteractionPolicy;

  // Ref bags
  refs: SequenceCanvasRefs;

  // Callback bags
  callbacks: SequenceCanvasCallbacks;
  dragCallbacks: SequenceCanvasDragCallbacks;
  messageDragCallbacks: SequenceCanvasMessageDragCallbacks;
}

function SequenceCanvasInner({
  participants,
  messages,
  fragments,
  participantGroups,
  layers,
  selectedItems,
  viewport,
  windowSize,
  palette,
  stickyMode,
  stickyTopHeaderCanvasY,
  stickyBottomHeaderCanvasY,
  stickyMaskBands,
  sequenceLifelineBottom,
  inlineEdit,
  inlineEdgeEdit,
  infoPanelParticipantId,
  sequenceDrag,
  fragmentDropParentId,
  isViewOnly,
  interactionPolicy,
  refs,
  callbacks,
  dragCallbacks,
  messageDragCallbacks,
}: SequenceCanvasProps) {
  // Destructure refs
  const {
    isDraggingParticipantRef,
    isDraggingMessageRef,
    isDraggingFragmentRef,
    activeFragmentResizeRef,
    activeAltDividerDragRef,
    fragmentResizeBaselineRef,
    altDividerDragBaselineRef,
    messageDragBaselineRef,
    dragReorderSnapshotRef,
    dragSelectedMessageIdsRef,
    dragStartMessageYRef,
    reorderMessageDragKeyRef,
  } = refs;

  // Destructure callbacks
  const {
    selectItem,
    setContextMenu,
    setInfoPanelFragmentId,
    snapshotBeforeMutation,
    moveFragment,
    setFragmentDropParentId,
    setInlineEdit,
    getSequenceMessageFootprint,
  } = callbacks;

  const {
    captureSequenceFragmentDragBaseline,
    restoreSequenceFragmentDragBaseline,
    beginSequenceDragPerf,
    recordSequenceDragPerf,
    endSequenceDragPerf,
    scheduleFragmentResizeMove,
    cancelScheduledFragmentResizeMove,
    applyFragmentEndDrag,
    scheduleAltDividerDragMove,
    cancelScheduledAltDividerDragMove,
    applyAltDividerDrag,
  } = dragCallbacks;

  const sequenceMessageNumbering = useSettingsStore((s) => s.sequenceMessageNumbering ?? true);
  const sequenceMonochromeViewer = useSettingsStore((s) => s.sequenceMonochromeViewer ?? false);
  const sequenceMonochromePaper = useSettingsStore((s) => s.sequenceMonochromePaper ?? 'white');
  const appearanceMode = resolveSequenceAppearanceMode({
    viewerEnabled: sequenceMonochromeViewer,
    paper: sequenceMonochromePaper,
  });
  const sequencePalette = resolveSequencePalette(appearanceMode, palette);
  const messageNumbers = useMemo(
    () => (sequenceMessageNumbering ? assignSequenceMessageNumbers(messages) : null),
    [messages, sequenceMessageNumbering],
  );

  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;
  const dragCallbacksRef = useRef(dragCallbacks);
  dragCallbacksRef.current = dragCallbacks;
  const messageDragCallbacksRef = useRef(messageDragCallbacks);
  messageDragCallbacksRef.current = messageDragCallbacks;
  const infoPanelParticipantIdRef = useRef(infoPanelParticipantId);
  infoPanelParticipantIdRef.current = infoPanelParticipantId;
  const interactionPolicyRef = useRef(interactionPolicy);
  interactionPolicyRef.current = interactionPolicy;
  const isViewOnlyRef = useRef(isViewOnly);
  isViewOnlyRef.current = isViewOnly;
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const pendingMessageDragRef = useRef<{ id: string; rawY: number } | null>(null);
  const messageDragRafRef = useRef<number | null>(null);

  const applyMessageDragMove = useCallback((id: string, rawY: number) => {
    const cb = callbacksRef.current;
    const dragCb = dragCallbacksRef.current;
    const msgDragCb = messageDragCallbacksRef.current;
    const perfStart = performance.now();
    let movedMessagesCount = 0;
    let updatedFragmentsCount = 0;
    if (isDraggingMessageRef.current !== id) {
      isDraggingMessageRef.current = id;
      cb.snapshotBeforeMutation();
      const dragSnapshot = buildMessageRowSnapshot(
        useDiagramStore.getState().messages,
        id,
      );
      dragReorderSnapshotRef.current = dragSnapshot;
      dragSelectedMessageIdsRef.current = msgDragCb.resolveDraggedMessageIds(
        dragSnapshot.orderedIds,
        id,
      );
      messageDragBaselineRef.current = dragCb.captureSequenceFragmentDragBaseline(id);
      dragCb.beginSequenceDragPerf('message', id);
    }

    const baseline = messageDragBaselineRef.current;
    if (baseline) {
      dragCb.restoreSequenceFragmentDragBaseline(baseline);
    }

    const liveState = useDiagramStore.getState();
    const liveFragments = liveState.fragments;
    const dragMsg = liveState.messages.find(m => m.id === id);
    if (!dragMsg) return;
    if (dragStartMessageYRef.current?.id !== id) {
      dragStartMessageYRef.current = { id, y: dragMsg.y };
    }
    let constrainedY = rawY;

    const isFragAncestor = (candidateAncestorId: string, fragId: string | undefined): boolean => {
      if (!fragId) return false;
      const f = liveFragments.find(ff => ff.id === fragId);
      if (!f?.parentFragmentId) return false;
      if (f.parentFragmentId === candidateAncestorId) return true;
      return isFragAncestor(candidateAncestorId, f.parentFragmentId);
    };

    if (!reorderMessageDragKeyRef.current) {
      for (const frag of liveFragments) {
        if (frag.id === dragMsg.fragmentId) continue;
        if (isFragAncestor(frag.id, dragMsg.fragmentId)) continue;
        if (dragMsg.fragmentId && isFragAncestor(dragMsg.fragmentId, frag.id)) continue;
        if (constrainedY > frag.startY && constrainedY < frag.endY) {
          const distTop = Math.abs(constrainedY - frag.startY);
          const distBot = Math.abs(constrainedY - frag.endY);
          constrainedY = distTop < distBot ? frag.startY - 10 : frag.endY + 10;
        }
      }
    }

    if (dragMsg.fragmentId) {
      const dragStartY = dragStartMessageYRef.current?.id === id
        ? dragStartMessageYRef.current.y
        : dragMsg.y;

      let currentFrag = liveFragments.find((f) => f.id === dragMsg.fragmentId);
      while (currentFrag) {
        if (currentFrag.type === 'alt') {
          const dividerY = currentFrag.altDividerY ?? (currentFrag.startY + (currentFrag.endY - currentFrag.startY) / 2);
          const footprint = cb.getSequenceMessageFootprint(dragMsg.label, dragMsg.sourceId, dragMsg.targetId);
          const topClearance = footprint.below + ALT_DIVIDER_MESSAGE_PADDING;
          const bottomClearance = footprint.above + ALT_DIVIDER_MESSAGE_PADDING;
          if (dragStartY < dividerY && constrainedY >= dividerY - topClearance) {
            const requestedDividerY = constrainedY + topClearance;
            const metrics = dragCb.applyAltDividerDrag(currentFrag.id, requestedDividerY, 'push');
            movedMessagesCount += metrics.movedMessages;
            updatedFragmentsCount += metrics.updatedFragments;
          } else if (dragStartY >= dividerY && constrainedY < dividerY + bottomClearance) {
            constrainedY = dividerY + bottomClearance;
          }
        }
        currentFrag = currentFrag.parentFragmentId
          ? liveFragments.find((f) => f.id === currentFrag!.parentFragmentId)
          : undefined;
      }
    }

    let dragMessageUpdates = 0;
    if (reorderMessageDragKeyRef.current) {
      dragMessageUpdates = msgDragCb.applyMessageDragWithReorder(id, constrainedY);
    } else {
      dragMessageUpdates = msgDragCb.applyMessageDragWithPush(id, constrainedY);
    }
    movedMessagesCount += dragMessageUpdates;
    if (!reorderMessageDragKeyRef.current) {
      updatedFragmentsCount += msgDragCb.ensureDraggedMessageFragmentExtents(dragSelectedMessageIdsRef.current);
    }
    dragCb.recordSequenceDragPerf(
      'message',
      performance.now() - perfStart,
      movedMessagesCount,
      updatedFragmentsCount,
    );
  }, [
    dragReorderSnapshotRef,
    dragSelectedMessageIdsRef,
    dragStartMessageYRef,
    isDraggingMessageRef,
    messageDragBaselineRef,
    reorderMessageDragKeyRef,
  ]);

  const flushScheduledMessageDragMove = useCallback(() => {
    messageDragRafRef.current = null;
    const pending = pendingMessageDragRef.current;
    pendingMessageDragRef.current = null;
    if (!pending) return;
    applyMessageDragMove(pending.id, pending.rawY);
  }, [applyMessageDragMove]);

  const scheduleMessageDragMove = useCallback((id: string, rawY: number) => {
    pendingMessageDragRef.current = { id, rawY };
    if (messageDragRafRef.current !== null) return;
    messageDragRafRef.current = window.requestAnimationFrame(flushScheduledMessageDragMove);
  }, [flushScheduledMessageDragMove]);

  const onMessageDragComplete = useCallback((id: string) => {
    if (messageDragRafRef.current !== null) {
      window.cancelAnimationFrame(messageDragRafRef.current);
      messageDragRafRef.current = null;
    }
    const pending = pendingMessageDragRef.current;
    pendingMessageDragRef.current = null;
    if (pending) applyMessageDragMove(pending.id, pending.rawY);

    const msgDragCb = messageDragCallbacksRef.current;
    const dragCb = dragCallbacksRef.current;
    const draggedMessageIds = dragSelectedMessageIdsRef.current.length > 0
      ? dragSelectedMessageIdsRef.current
      : [id];
    isDraggingMessageRef.current = null;
    dragStartMessageYRef.current = null;
    dragReorderSnapshotRef.current = null;
    dragSelectedMessageIdsRef.current = [];
    messageDragBaselineRef.current = null;
    msgDragCb.finalizeDraggedMessages(draggedMessageIds, id);
    dragCb.endSequenceDragPerf('message');
  }, [
    applyMessageDragMove,
    dragReorderSnapshotRef,
    dragSelectedMessageIdsRef,
    dragStartMessageYRef,
    isDraggingMessageRef,
    messageDragBaselineRef,
  ]);

  useEffect(() => () => {
    if (messageDragRafRef.current !== null) {
      window.cancelAnimationFrame(messageDragRafRef.current);
      messageDragRafRef.current = null;
    }
    pendingMessageDragRef.current = null;
  }, []);

  const onMessageClick = useCallback((id: string, e: KonvaInteractionEvent) => {
    const evt = e?.evt as MouseEvent | TouchEvent | undefined;
    callbacksRef.current.selectItem(id, evt?.shiftKey ?? false);
  }, []);

  const onMessageDoubleClick = useCallback((id: string) => {
    if (!isViewOnlyRef.current) callbacksRef.current.setInlineEdgeEdit({ edgeId: id });
  }, []);

  const onMessageHover = useCallback((id: string | null, sx: number, sy: number) => {
    const cb = callbacksRef.current;
    if (id) {
      cb.handleSidebarHoverStart('message', id);
      const msg = messagesRef.current.find(m => m.id === id);
      if (msg?.tooltip) {
        cb.setSeqMsgTooltip(prev => (
          prev && prev.msgId === id && prev.screenX === sx && prev.screenY === sy
            ? prev
            : { msgId: id, screenX: sx, screenY: sy }
        ));
      }
      return;
    }
    cb.setSeqMsgTooltip(null);
    cb.handleSidebarHoverEnd('message');
  }, []);

  const onMessageContextMenu = useCallback((id: string, x: number, y: number) => {
    if (!isViewOnlyRef.current) callbacksRef.current.setContextMenu({ type: 'seqMessage', x, y, messageId: id });
  }, []);

  const onParticipantClick = useCallback((id: string, e: KonvaInteractionEvent) => {
    e.cancelBubble = true;
    const cb = callbacksRef.current;
    cb.selectItem(id, (e?.evt as MouseEvent)?.shiftKey ?? false);
    if (infoPanelParticipantIdRef.current) cb.setInfoPanelParticipantId(id);
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onProxyParticipantClick = useCallback((id: string, _e: KonvaInteractionEvent) => {
    callbacksRef.current.selectItem(id);
  }, []);

  const onParticipantDoubleClick = useCallback((id: string) => {
    if (interactionPolicyRef.current.canMutateStructure) {
      callbacksRef.current.setInlineEdit({ nodeId: id, field: 'label' });
    }
  }, []);

  const onParticipantDragMove = useCallback((id: string, newX: number) => {
    if (!interactionPolicyRef.current.canRearrangeParticipants) return;
    if (isDraggingParticipantRef.current !== id) {
      isDraggingParticipantRef.current = id;
      callbacksRef.current.snapshotBeforeMutation();
    }
    callbacksRef.current.updateParticipant(id, { x: newX });
  }, [isDraggingParticipantRef]);

  const onParticipantDragEnd = useCallback((id: string, newX: number) => {
    if (!interactionPolicyRef.current.canRearrangeParticipants) return;
    isDraggingParticipantRef.current = null;
    callbacksRef.current.updateParticipant(id, { x: newX });
    callbacksRef.current.reorderParticipants();
  }, [isDraggingParticipantRef]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onProxyParticipantDragEnd = useCallback((_id: string, _newX: number) => {}, []);

  const onParticipantContextMenu = useCallback((id: string, x: number, y: number) => {
    if (!isViewOnlyRef.current) {
      callbacksRef.current.setContextMenu({ type: 'participant', x, y, participantId: id });
    }
  }, []);

  const onParticipantHover = useCallback((id: string | null) => {
    const cb = callbacksRef.current;
    if (id) cb.handleSidebarHoverStart('participant', id);
    else cb.handleSidebarHoverEnd('participant');
  }, []);

  const onLifelineMouseDown = useCallback((participantId: string, y: number) => {
    callbacksRef.current.handleLifelineMouseDown(participantId, y);
  }, []);

  const onLifelineMouseUp = useCallback((participantId: string, y: number) => {
    callbacksRef.current.handleLifelineMouseUp(participantId, y);
  }, []);

  const lifelineBottom = sequenceLifelineBottom;
  const selectedSet = useMemo(() => new Set(selectedItems), [selectedItems]);
  const layerById = useMemo(() => new Map(layers.map(layer => [layer.id, layer])), [layers]);

  const getParticipantLayerOpacity = useCallback((p: { layerId?: string }) => {
    const layer = layerById.get(p.layerId ?? 'default') ?? layerById.get('default');
    if (!layer) return 1;
    if (!layer.visible) return 0;
    return layer.dimmed ? 0.2 : 1;
  }, [layerById]);
  const isParticipantVisible = useCallback((p: { layerId?: string }) => {
    const layer = layerById.get(p.layerId ?? 'default');
    return layer?.visible ?? true;
  }, [layerById]);

  const collapsedPGroupIds = useMemo(
    () => new Set(participantGroups.filter(g => g.collapsed).map(g => g.id)),
    [participantGroups],
  );
  const collapsedParticipantIds = useMemo(
    () => new Set(participantGroups.filter(g => g.collapsed).flatMap(g => g.childParticipantIds)),
    [participantGroups],
  );
  const visibleParticipants = useMemo(
    () => participants.filter(p => isParticipantVisible(p) && !collapsedParticipantIds.has(p.id)),
    [collapsedParticipantIds, isParticipantVisible, participants],
  );

  const proxyParticipants = useMemo(() => (
    participantGroups
      .filter(g => g.collapsed)
      .map(g => {
        const children = participants.filter(p => g.childParticipantIds.includes(p.id));
        if (children.length === 0) return null;
        const avgX = children.reduce((sum, p) => sum + p.x, 0) / children.length;
        return {
          group: g,
          proxy: {
            id: g.id,
            type: children[0].type,
            label: g.label,
            color: g.color,
            order: Math.min(...children.map(c => c.order)),
            x: avgX,
          } as SequenceParticipant,
        };
      })
      .filter(Boolean) as { group: typeof participantGroups[0]; proxy: SequenceParticipant }[]
  ), [participantGroups, participants]);

  const allVisibleParticipants = useMemo(
    () => [...visibleParticipants, ...proxyParticipants.map(pp => pp.proxy)],
    [proxyParticipants, visibleParticipants],
  );

  const participantById = useMemo(
    () => new Map(allVisibleParticipants.map(p => [p.id, p])),
    [allVisibleParticipants],
  );
  const collapsedGroupByChildId = useMemo(() => {
    const map = new Map<string, string>();
    participantGroups.forEach(g => {
      if (!g.collapsed) return;
      g.childParticipantIds.forEach(childId => map.set(childId, g.id));
    });
    return map;
  }, [participantGroups]);

  const resolveParticipant = useCallback((pId: string) => {
    if (collapsedParticipantIds.has(pId)) {
      const groupId = collapsedGroupByChildId.get(pId);
      if (groupId) return participantById.get(groupId) ?? null;
    }
    return participantById.get(pId) ?? null;
  }, [collapsedGroupByChildId, collapsedParticipantIds, participantById]);

  const selfMessageLabelWidthByParticipantId = useMemo(() => (
    new Map<string, number>(
      allVisibleParticipants.map((participant) => ([
        participant.id,
        resolveSequenceMessageLabelWidth(
          allVisibleParticipants,
          participant.id,
          participant.id,
          SEQUENCE_MIN_LABEL_WIDTH,
        ),
      ])),
    )
  ), [allVisibleParticipants]);

  const overlayRect = useMemo(
    () => visibleCanvasRect(viewport, windowSize),
    [viewport, windowSize],
  );
  const topMaskRect = stickyMaskBands.top
    ? {
      y: screenToCanvasY(stickyMaskBands.top.y, viewport.y, viewport.scale),
      h: stickyMaskBands.top.height / Math.max(0.0001, viewport.scale),
    }
    : null;
  const topViewportCanvasY = topMaskRect
    ? screenToCanvasY(0, viewport.y, viewport.scale)
    : 0;
  const topMaskExtensionH = topMaskRect
    ? Math.max(0, topMaskRect.y - topViewportCanvasY)
    : 0;
  const bottomMaskRect = stickyMaskBands.bottom
    ? {
      y: screenToCanvasY(stickyMaskBands.bottom.y, viewport.y, viewport.scale),
      h: stickyMaskBands.bottom.height / Math.max(0.0001, viewport.scale),
    }
    : null;
  const topMaskSolidH = topMaskRect ? Math.max(1, topMaskRect.h * 0.72) : 0;
  const bottomMaskSolidH = bottomMaskRect ? Math.max(1, bottomMaskRect.h * 0.72) : 0;
  const maskStrong = toRgba(sequencePalette.canvasBg, 0.94);
  const maskMid = toRgba(sequencePalette.canvasBg, 0.82);
  const maskClear = toRgba(sequencePalette.canvasBg, 0);

  return (
    <>
      {/* Participant group boundaries (expanded, behind everything) */}
      {participantGroups.filter(g => !g.collapsed).map(g => {
        const children = participants.filter(p => g.childParticipantIds.includes(p.id) && isParticipantVisible(p));
        if (children.length === 0) return null;
        const minX = Math.min(...children.map(p => p.x)) - 20;
        const maxX = Math.max(...children.map(p => p.x + 140)) + 20;
        const groupWidth = maxX - minX;
        const groupColor = appearanceMode === 'color' ? g.color : sequencePalette.edgeDefault;
        return (
          <Group key={g.id}>
            <Rect
              x={minX}
              y={20}
              width={groupWidth}
              height={lifelineBottom + 60}
              fill={`${groupColor}08`}
              stroke={selectedSet.has(g.id) ? '#a855f7' : `${groupColor}40`}
              strokeWidth={selectedSet.has(g.id) ? 2 : 1.5}
              dash={[8, 4]}
              cornerRadius={8}
              perfectDrawEnabled={false}
              shadowForStrokeEnabled={false}
              onClick={() => selectItem(g.id)}
              onDblClick={() => {
                if (!interactionPolicy.canMutateStructure) return;
                setInlineEdit({ nodeId: g.id, field: 'label' });
              }}
              onContextMenu={(e) => {
                if (isViewOnly) return;
                e.evt.preventDefault();
                const stage = e.target.getStage();
                const pos = stage?.getPointerPosition();
                if (pos) setContextMenu({ type: 'group', x: pos.x, y: pos.y, groupId: g.id });
              }}
            />
            <Rect
              x={minX}
              y={20}
              width={Math.min(120, groupWidth)}
              height={22}
              fill={groupColor}
              cornerRadius={[8, 0, 8, 0]}
              listening={false}
              perfectDrawEnabled={false}
            />
            <Text
              text={inlineEdit?.nodeId === g.id && inlineEdit?.field === 'label' ? '' : g.label}
              x={minX + 8}
              y={20}
              height={22}
              verticalAlign="middle"
              fontFamily={FONT_FAMILY}
              fontSize={10}
              fontStyle="bold"
              fill={appearanceMode === 'color' ? 'white' : sequencePalette.canvasBg}
              listening={false}
              perfectDrawEnabled={false}
            />
          </Group>
        );
      })}

      {/* Fragments (behind messages) — sorted so parents render before children */}
      {(() => {
        // Topological sort: render parents before children (correct z-order)
        const sortedFrags: typeof fragments = [];
        const visited = new Set<string>();
        const visitFrag = (frag: typeof fragments[0]) => {
          if (visited.has(frag.id)) return;
          if (frag.parentFragmentId) {
            const parent = fragments.find(f => f.id === frag.parentFragmentId);
            if (parent) visitFrag(parent);
          }
          visited.add(frag.id);
          sortedFrags.push(frag);
        };
        fragments.forEach(visitFrag);

        // Compute nest level for a fragment (depth in parent chain)
        const getNestLevel = (fragId: string, depth = 0): number => {
          const f = fragments.find(ff => ff.id === fragId);
          if (!f?.parentFragmentId) return depth;
          return getNestLevel(f.parentFragmentId, depth + 1);
        };

        return sortedFrags.map((frag) => {
          const fragParticipants = participants.filter(p => frag.participantIds.includes(p.id));
          if (fragParticipants.length === 0) return null;
          const nestLevel = getNestLevel(frag.id);
          const inset = nestLevel * 12;
          const minX = Math.min(...fragParticipants.map(p => p.x));
          const maxX = Math.max(...fragParticipants.map(p => p.x + 140));
          const fragX = minX - 20 + inset;
          const fragWidth = maxX - minX + 40 - inset * 2;

          // Keep ALT divider stable to fragment geometry (prevents branch drift while dragging messages).
          let altDividerY: number | undefined;
          let altDividerBounds: { minY: number; maxY: number; strictMinY?: number; strictMaxY?: number } | undefined;
          if (frag.type === 'alt') {
            altDividerY = frag.altDividerY ?? (frag.startY + (frag.endY - frag.startY) / 2);
            const looseMinY = frag.startY + ALT_DIVIDER_TOP_GAP;
            const looseMaxY = frag.endY - ALT_DIVIDER_BOTTOM_GAP;
            let strictMinY = looseMinY;
            let strictMaxY = looseMaxY;

            // Build branch constraints from all messages in this fragment subtree.
            const fragmentById = new Map(fragments.map((f) => [f.id, f]));
            const scopedMessages = messages.filter((message) => isInsideFragmentTree(message.fragmentId, frag.id, fragmentById));
            const topBranch = scopedMessages.filter((message) => message.y < altDividerY!);
            const bottomBranch = scopedMessages.filter((message) => message.y >= altDividerY!);
            if (topBranch.length > 0) {
              strictMinY = Math.max(
                strictMinY,
                Math.max(...topBranch.map((message) => {
                  const footprint = getSequenceMessageFootprint(message.label, message.sourceId, message.targetId);
                  return message.y + footprint.below + ALT_DIVIDER_MESSAGE_PADDING;
                })),
              );
            }
            if (bottomBranch.length > 0) {
              strictMaxY = Math.min(
                strictMaxY,
                Math.min(...bottomBranch.map((message) => {
                  const footprint = getSequenceMessageFootprint(message.label, message.sourceId, message.targetId);
                  return message.y - footprint.above - ALT_DIVIDER_MESSAGE_PADDING;
                })),
              );
            }
            if (strictMaxY < strictMinY) {
              const lockY = Math.max(looseMinY, Math.min(looseMaxY, altDividerY));
              strictMinY = lockY;
              strictMaxY = lockY;
            }
            altDividerBounds = {
              minY: looseMinY,
              maxY: looseMaxY,
              strictMinY,
              strictMaxY,
            };
          }

          // Parent bounds for nested drag constraints
          const parentBounds = frag.parentFragmentId
            ? (() => {
                const p = fragments.find(f => f.id === frag.parentFragmentId);
                return p ? { startY: p.startY, endY: p.endY } : undefined;
              })()
            : undefined;

          // Same-level peers for collision avoidance during drag
          const peerBounds = fragments
            .filter(f => f.id !== frag.id && f.parentFragmentId === frag.parentFragmentId)
            .map(f => ({ id: f.id, startY: f.startY, endY: f.endY }));

          return (
            <FragmentComponent
              key={frag.id}
              fragment={frag}
              x={fragX}
              width={fragWidth}
              viewport={viewport}
                    isSelected={selectedSet.has(frag.id)}
              onFragmentClick={(id, e) => {
                const evt = e?.evt as MouseEvent | TouchEvent | undefined;
                selectItem(id, evt?.shiftKey ?? false);
              }}
              onFragmentDoubleClick={(id) => { if (!isViewOnly) setInfoPanelFragmentId(id); }}
              onFragmentDragStart={(id) => {
                if (isDraggingFragmentRef.current !== id) {
                  isDraggingFragmentRef.current = id;
                  snapshotBeforeMutation();
                }
              }}
              onFragmentDragMove={(id, currentY) => {
                if (currentY < 0) {
                  // drag ended — clear hint
                  setFragmentDropParentId(null);
                  return;
                }
                const dragged = fragments.find(ff => ff.id === id);
                if (!dragged) return;
                const h = dragged.endY - dragged.startY;
                // Find a fragment that would fully contain the dragged one → nesting target
                const potential = innermostContainingFragment(
                  fragments,
                  currentY,
                  currentY + h,
                  { excludeId: id },
                );
                const nextParentId = potential?.id ?? null;
                setFragmentDropParentId((prev) => (prev === nextParentId ? prev : nextParentId));
              }}
              onFragmentDragEnd={(id, newStartY) => {
                isDraggingFragmentRef.current = null;
                setFragmentDropParentId(null);
                const f = fragments.find(ff => ff.id === id);
                if (!f) return;
                const deltaY = newStartY - f.startY;
                moveFragment(id, deltaY);
              }}
              altDividerY={altDividerY}
              altDividerBounds={altDividerBounds}
              palette={sequencePalette}
              appearanceMode={appearanceMode}
              nestLevel={nestLevel}
              parentBounds={parentBounds}
              peerBounds={peerBounds}
              isPotentialParent={fragmentDropParentId === frag.id}
              onResizeDragStart={(fragId) => {
                if (activeFragmentResizeRef.current !== fragId) {
                  activeFragmentResizeRef.current = fragId;
                  snapshotBeforeMutation();
                  fragmentResizeBaselineRef.current = captureSequenceFragmentDragBaseline(fragId);
                  beginSequenceDragPerf('fragmentResize', fragId);
                }
              }}
              onResizeDragMove={(fragId, newEndY, mode) => {
                scheduleFragmentResizeMove(fragId, newEndY, mode);
              }}
              onResizeEnd={(fragId, newEndY, mode) => {
                cancelScheduledFragmentResizeMove();
                const baseline = fragmentResizeBaselineRef.current;
                if (baseline?.fragId === fragId) {
                  restoreSequenceFragmentDragBaseline(baseline);
                }
                const t0 = performance.now();
                const metrics = applyFragmentEndDrag(fragId, newEndY, mode);
                recordSequenceDragPerf('fragmentResize', performance.now() - t0, metrics.movedMessages, metrics.updatedFragments);
                activeFragmentResizeRef.current = null;
                fragmentResizeBaselineRef.current = null;
                endSequenceDragPerf('fragmentResize');
              }}
              onAltDividerDragStart={(fragId) => {
                if (activeAltDividerDragRef.current !== fragId) {
                  activeAltDividerDragRef.current = fragId;
                  snapshotBeforeMutation();
                  altDividerDragBaselineRef.current = captureSequenceFragmentDragBaseline(fragId);
                  beginSequenceDragPerf('altDivider', fragId);
                }
              }}
              onAltDividerDragMove={(fragId, newDividerY, mode) => {
                scheduleAltDividerDragMove(fragId, newDividerY, mode);
              }}
              onAltDividerDragEnd={(fragId, newDividerY, mode) => {
                cancelScheduledAltDividerDragMove();
                const baseline = altDividerDragBaselineRef.current;
                if (baseline?.fragId === fragId) {
                  restoreSequenceFragmentDragBaseline(baseline);
                }
                const t0 = performance.now();
                const metrics = applyAltDividerDrag(fragId, newDividerY, mode);
                recordSequenceDragPerf('altDivider', performance.now() - t0, metrics.movedMessages, metrics.updatedFragments);
                activeAltDividerDragRef.current = null;
                altDividerDragBaselineRef.current = null;
                endSequenceDragPerf('altDivider');
              }}
              isReadOnly={isViewOnly}
            />
          );
        });
      })()}

      {/* Messages — filter by participant visibility + collapsed groups */}
      {messages.map((msg) => {
        const srcP = resolveParticipant(msg.sourceId);
        const tgtP = resolveParticipant(msg.targetId);
        if (!srcP || !tgtP) return null;
        // Hide internal messages within a collapsed group
        if (srcP.id === tgtP.id && collapsedPGroupIds.has(srcP.id)) return null;
        if (!isParticipantVisible(participants.find(p => p.id === msg.sourceId) ?? { layerId: undefined })) return null;
        if (!isParticipantVisible(participants.find(p => p.id === msg.targetId) ?? { layerId: undefined })) return null;
        const srcX = srcP.x + 70;
        const tgtX = tgtP.x + 70;
        const srcLayer = getParticipantLayerOpacity(participants.find(p => p.id === msg.sourceId) ?? { layerId: undefined });
        const tgtLayer = getParticipantLayerOpacity(participants.find(p => p.id === msg.targetId) ?? { layerId: undefined });
        const msgOpacity = Math.min(srcLayer, tgtLayer);
        const selfMessageLabelMaxWidth = msg.sourceId === msg.targetId
          ? (selfMessageLabelWidthByParticipantId.get(srcP.id) ?? SEQUENCE_MIN_LABEL_WIDTH)
          : undefined;
        return (
          <MessageComponent
            key={msg.id}
            message={{
              ...msg,
              color: resolveMessageColor(msg.color, appearanceMode, sequencePalette),
            }}
            sourceX={srcX}
            targetX={tgtX}
            selfMessageLabelMaxWidth={selfMessageLabelMaxWidth}
            isSelected={selectedSet.has(msg.id)}
            onMessageClick={onMessageClick}
            onMessageDoubleClick={onMessageDoubleClick}
            onMessageHover={onMessageHover}
            onMessageContextMenu={onMessageContextMenu}
            onMessageDragEnd={scheduleMessageDragMove}
            onMessageDragComplete={onMessageDragComplete}
            hideLabel={inlineEdgeEdit?.edgeId === msg.id}
            palette={sequencePalette}
            zoomScale={viewport.scale}
            opacity={msgOpacity}
            isReadOnly={isViewOnly}
            hasTooltip={Boolean(msg.tooltip)}
            sequenceNumber={messageNumbers?.get(msg.id)}
          />
        );
      })}

      {/* Temp drag line for message creation */}
      {sequenceDrag && sequenceDrag.currentX !== undefined && (
        <Line
          points={[sequenceDrag.sourceX, sequenceDrag.startY, sequenceDrag.currentX, sequenceDrag.startY]}
          stroke={sequencePalette.edgeSelected}
          strokeWidth={2}
          dash={[8, 5]}
          listening={false}
          perfectDrawEnabled={false}
          shadowForStrokeEnabled={false}
          opacity={0.85}
        />
      )}

      {topMaskRect && (
        <>
          {topMaskExtensionH > 0 && (
            <Rect
              x={overlayRect.x}
              y={topViewportCanvasY}
              width={overlayRect.width}
              height={topMaskExtensionH}
              listening={false}
              perfectDrawEnabled={false}
              fill={maskStrong}
            />
          )}
          <Rect
            x={overlayRect.x}
            y={topMaskRect.y}
            width={overlayRect.width}
            height={topMaskSolidH}
            listening={false}
            perfectDrawEnabled={false}
            fill={maskStrong}
          />
          <Rect
            x={overlayRect.x}
            y={topMaskRect.y + topMaskSolidH}
            width={overlayRect.width}
            height={Math.max(1, topMaskRect.h - topMaskSolidH)}
            listening={false}
            perfectDrawEnabled={false}
            fillLinearGradientStartPoint={{ x: 0, y: topMaskRect.y + topMaskSolidH }}
            fillLinearGradientEndPoint={{ x: 0, y: topMaskRect.y + topMaskRect.h }}
            fillLinearGradientColorStops={[0, maskMid, 1, maskClear]}
          />
        </>
      )}

      {bottomMaskRect && (
        <>
          <Rect
            x={overlayRect.x}
            y={bottomMaskRect.y}
            width={overlayRect.width}
            height={Math.max(1, bottomMaskRect.h - bottomMaskSolidH)}
            listening={false}
            perfectDrawEnabled={false}
            fillLinearGradientStartPoint={{ x: 0, y: bottomMaskRect.y }}
            fillLinearGradientEndPoint={{ x: 0, y: bottomMaskRect.y + Math.max(1, bottomMaskRect.h - bottomMaskSolidH) }}
            fillLinearGradientColorStops={[0, maskClear, 1, maskMid]}
          />
          <Rect
            x={overlayRect.x}
            y={bottomMaskRect.y + Math.max(1, bottomMaskRect.h - bottomMaskSolidH)}
            width={overlayRect.width}
            height={bottomMaskSolidH}
            listening={false}
            perfectDrawEnabled={false}
            fill={maskStrong}
          />
        </>
      )}

      {/* Proxy participants for collapsed groups */}
      {proxyParticipants.map(({ proxy }) => (
        <ParticipantComponent
          key={`proxy-${proxy.id}`}
          participant={{
            ...proxy,
            color: resolveParticipantColor(proxy.color, appearanceMode, sequencePalette),
          }}
          lifelineBottom={lifelineBottom}
          topHeaderY={stickyTopHeaderCanvasY}
          bottomHeaderY={stickyBottomHeaderCanvasY}
          stickyScale={stickyMode.stickyScale}
          topPinned={stickyMode.topPinned}
          bottomPinned={stickyMode.bottomPinned}
          isSelected={selectedSet.has(proxy.id)}
          onParticipantClick={onProxyParticipantClick}
          onParticipantDoubleClick={onParticipantDoubleClick}
          onParticipantDragEnd={onProxyParticipantDragEnd}
          onLifelineMouseDown={onLifelineMouseDown}
          onLifelineMouseUp={onLifelineMouseUp}
          palette={sequencePalette}
          hideLabel={inlineEdit?.nodeId === proxy.id && inlineEdit?.field === 'label'}
          isReadOnly={isViewOnly}
          canDrag={false}
        />
      ))}

      {/* Participants (on top of messages) */}
      {visibleParticipants.map((p) => (
        <ParticipantComponent
          key={p.id}
          participant={{
            ...p,
            color: resolveParticipantColor(p.color, appearanceMode, sequencePalette),
          }}
          lifelineBottom={lifelineBottom}
          topHeaderY={stickyTopHeaderCanvasY}
          bottomHeaderY={stickyBottomHeaderCanvasY}
          stickyScale={stickyMode.stickyScale}
          topPinned={stickyMode.topPinned}
          bottomPinned={stickyMode.bottomPinned}
          isSelected={selectedSet.has(p.id)}
          onParticipantClick={onParticipantClick}
          onParticipantDoubleClick={onParticipantDoubleClick}
          onParticipantDragMove={onParticipantDragMove}
          onParticipantDragEnd={onParticipantDragEnd}
          onParticipantContextMenu={onParticipantContextMenu}
          onParticipantHover={onParticipantHover}
          onLifelineMouseDown={onLifelineMouseDown}
          onLifelineMouseUp={onLifelineMouseUp}
          palette={sequencePalette}
          layerOpacity={getParticipantLayerOpacity(p)}
          hideLabel={inlineEdit?.nodeId === p.id && inlineEdit?.field === 'label'}
          isReadOnly={isViewOnly}
          canDrag={interactionPolicy.canRearrangeParticipants}
        />
      ))}
    </>
  );
}

export const SequenceCanvas = React.memo(SequenceCanvasInner);
